import debounce from "./debounce";
// Conditionally import StickyHeader only in browser (not SSR)
// In SSR, this will be undefined and scroll functions will safely no-op
const StickyHeader = typeof document !== "undefined"
    ? require("../components/StickyHeader").default
    : undefined;
function getAnchorFromLink(link) {
    return link.getAttribute("data-target") || link.getAttribute("href");
}
/**
 * Get target element from link URL or target ID.
 */
function getTargetFromHashLink(link) {
    const anchor = getAnchorFromLink(link);
    const el = document.getElementById(anchor.split("#")[1]);
    return el ? el : null;
}
/**
 * Scroll to specific element.
 */
export function scrollToElement(target, behavior = "smooth", disableDebounce = false) {
    if (!target)
        return;
    const scrollToElementEvent = new Event("to-element.restaumatic.scroll", {
        bubbles: false,
        cancelable: true,
        composed: true,
    });
    document.dispatchEvent(scrollToElementEvent);
    // TODO: Refactor this function to eliminate the "scrollMarginTop" logic
    const { stickyHeaderElement } = (StickyHeader === null || StickyHeader === void 0 ? void 0 : StickyHeader.data) || {};
    const handleResizeAndScroll = debounce(() => {
        // Compute scroll margin of the target element
        let scrollMargin = 0;
        if (stickyHeaderElement && (StickyHeader === null || StickyHeader === void 0 ? void 0 : StickyHeader.utils.isHeaderSticky())) {
            scrollMargin = StickyHeader.utils.getStickyHeaderHeight();
        }
        // NOTICE: StickyTabs experiment, needs refactor
        const stickyTabsEl = document.querySelector(".m-flex-layout__aside--sticky-tabs");
        if (stickyTabsEl && /^menu/.test(target.id)) {
            scrollMargin += stickyTabsEl.clientHeight;
        }
        // Apply scroll margin only on sites with sticky elements
        if (stickyHeaderElement || stickyTabsEl) {
            target.style.scrollMarginTop = `${scrollMargin}px`;
        }
        // Perform scroll
        requestAnimationFrame(() => {
            target.scrollIntoView({
                behavior,
                block: "start",
            });
        });
    }, disableDebounce ? 0 : 100);
    const resizeObserver = new ResizeObserver(handleResizeAndScroll);
    // Observe body to detect layout shifts due to loading images, showing sections dynamically etc.
    resizeObserver.observe(document.body);
    // Observe sticky header due to its height change and applying different scrollMarginTop to the target element
    if (stickyHeaderElement) {
        resizeObserver.observe(stickyHeaderElement);
    }
    // Clean up after reasonable time
    setTimeout(() => resizeObserver.disconnect(), 1000);
}
/**
 * Scroll to element if target is above our position (it's very likely that user expects it).
 * @param target The element to check and potentially scroll to
 * @param visibleThreshold Number of pixels from the top where element is still considered not visible (default: 0)
 */
export function scrollIfAbove(target, visibleThreshold = 0) {
    if (!target) {
        return false;
    }
    const rect = target.getBoundingClientRect();
    const offsetTop = rect.top;
    // Adjust distance by sticky header height
    const distanceTop = Math.round(offsetTop - ((StickyHeader === null || StickyHeader === void 0 ? void 0 : StickyHeader.utils.getStickyHeaderHeight()) || 0));
    // Scroll if:
    // 1. Element is above viewport (negative distance - completely out of view)
    // 2. OR element's top is less than the threshold pixels from viewport top (partially visible)
    if (distanceTop < visibleThreshold) {
        scrollToElement(target);
        return true;
    }
    return false;
}
/**
 * Scroll to element if target is below our position.
 * @param target The element to check and potentially scroll to
 * @param visibleThreshold Number of pixels from the bottom where element is still considered not visible (default: 0)
 */
export function scrollIfBelow(target, visibleThreshold = 0) {
    if (!target) {
        return false;
    }
    const rect = target.getBoundingClientRect();
    // Calculate if element is below viewport
    const isElementBelowViewport = rect.top > window.innerHeight;
    // Determine if the element is partially visible near the bottom edge of the viewport
    // The element is considered partially visible if less than visibleThreshold pixels are showing
    const isPartiallyVisible = window.innerHeight - rect.top < visibleThreshold;
    if (isElementBelowViewport || isPartiallyVisible) {
        scrollToElement(target);
        return true;
    }
    return false;
}
/**
 * Scroll to element if target is not visible (below or above our position).
 * @param target The element to check and potentially scroll to
 * @param topThreshold Number of pixels from the top where element is still considered not visible (default: 0)
 * @param bottomThreshold Number of pixels from the bottom where element is still considered not visible (default: 0)
 */
export function scrollIfNotVisible(target, topThreshold = 0, bottomThreshold = 0) {
    // Check if target element is within a sticky parent (eg. sticky dock or footer) - if so, don't scroll
    if (target.parentElement !== null) {
        const parentPosition = window.getComputedStyle(target.parentElement).position;
        if (parentPosition === "sticky" || parentPosition === "fixed") {
            return false;
        }
    }
    return (scrollIfAbove(target, topThreshold) ||
        scrollIfBelow(target, bottomThreshold));
}
/**
 * Init scroll utility for specific anchor links.
 */
export function initScroll(anchorPrefix = "section-") {
    const anchors = document.querySelectorAll(`a[href*="#${anchorPrefix}"]`);
    anchors.forEach((item) => {
        item.addEventListener("click", (e) => {
            const link = e.currentTarget;
            const target = getTargetFromHashLink(link);
            if (!target)
                return;
            history.pushState(null, "", getAnchorFromLink(link));
            scrollToElement(target);
            e.preventDefault();
        });
    });
}
/**
 * Scroll to the first error element within the parent element if not visible or flash it if visible.
 *
 * @param parentSelector Selector of the element to search within
 * @param errorSelectors Search for these elements within the parent (the order matters)
 */
export function scrollToError(parentSelector, errorSelectors) {
    // Get parent element
    const parentElement = document.querySelector(parentSelector);
    if (!parentElement) {
        return;
    }
    // Wait for errors render
    setTimeout(() => {
        // Get first visible error element
        const errorElement = errorSelectors
            .map((selector) => Array.from(parentElement.querySelectorAll(selector)))
            .flat()
            .filter((el) => {
            if (el && "checkVisibility" in el) {
                // @ts-ignore - `checkVisibility()` is modern method that is not yet included in TypeScript’s default HTMLElement type definitions
                return el.checkVisibility({ checkVisibilityCSS: true });
            }
            return false;
        })
            .shift();
        if (!errorElement) {
            return;
        }
        // Try to scroll if element is not visible or is partially visible within 140px from the bottom (accounting for sticky buttons/dock)
        const scrolled = scrollIfNotVisible(errorElement, 0, 140);
        // "Flash" the element if it's already visible
        if (!scrolled) {
            const root = window.document.querySelector(":root");
            if (!root) {
                return;
            }
            const themeBaseDuration = getComputedStyle(root).getPropertyValue("--base-duration") || "0.3"; // in s
            const animationDuration = parseFloat(themeBaseDuration) * 1000; // in ms
            errorElement.style.animation = `fadeIn ${animationDuration}ms`;
            setTimeout(() => {
                errorElement.style.animation = "";
            }, animationDuration);
        }
    }, 200);
}
